import { useState } from 'react';

function valueFromEvent(e: React.ChangeEvent<unknown>) : string|number { //TODO make it work with strings, numbers and enums
	return (e.target as any).value;
}

interface BaseStateAgg<S extends boolean|string|number> {
	name: string,
	value: S
	setter: (value: S) => void
}

function makeSetterFn<S>(value: S, setter: ((value: S) => void)) {
	return function(e: any) {
		if (typeof e?.stopPropagation === 'function') e.stopPropagation();
		setter(value);
	};
}

/**
 * Aggregates the value, the setter and boolean-specific handlers directly usable in buttons and checkboxes.
 */
export interface IBooleanStateAgg extends BaseStateAgg<boolean> {
	falseHandler: (event?: any) => void,
	trueHandler: (event?: any) => void,
	toggleHandler: (event?: any) => void,
}

export function useBooleanStateAgg(initialState: boolean=false, name: string='') : IBooleanStateAgg {
	const [value, setter] = useState(initialState);
	return {
		name,
		value,
		setter,
		falseHandler: makeSetterFn(false, setter),
		trueHandler: makeSetterFn(true, setter),
		toggleHandler: makeSetterFn(!value, setter),
	};
}

/**
 * Aggregates the value, the setter and number-specific handlers directly usable in text fields.
 */
export interface INumberStateAgg extends BaseStateAgg<number> {
	makeSetter: (value: number) => ((event?: any) => void)
	changeHandler: (e: React.ChangeEvent<{value: number}>) => void
}

export function useNumberStateAgg(initialState: number, name: string='') : INumberStateAgg {
	const [value, setter] = useState(initialState);
	return {
		name,
		value,
		setter,
		makeSetter: v => makeSetterFn(v, setter),
		changeHandler: (e: React.ChangeEvent<any>) => {
			e.stopPropagation();
			setter(+valueFromEvent(e));
		},
	};
}

type StringKeyOf<T> = Extract<keyof T, string>;
type EnumNumber<E> = Record<StringKeyOf<E>, number>;

/**
 * Aggregates the value, the setter and number-specific handlers directly usable with numeric enums in selects.
 */
export interface IEnumNumberStateAgg<E extends EnumNumber<E>, V extends number=number> extends BaseStateAgg<V> {
	makeSetter: (value: V) => ((event?: any) => void)
	changeHandler: (e: React.ChangeEvent<{value: number & keyof V}>) => void
}

export function useEnumNumberStateAgg<E extends EnumNumber<E>, V extends number=number>(anEnum: E, initialState: number , name: string='') : IEnumNumberStateAgg<E, V> {
	const [value, setter] = useState<V>(initialState as V);
	return {
		name,
		value,
		setter,
		makeSetter: v => makeSetterFn(v, setter),
		changeHandler: (e: React.ChangeEvent<any>) => {
			e.stopPropagation();
			setter(+valueFromEvent(e) as any); //TODO
		},
	};
}

/**
 * Aggregates the value, the setter and string-specific handlers directly usable in numeric fields.
 */
export interface IStringStateAgg extends BaseStateAgg<string> {
	makeSetter: (value: string) => ((event?: any) => void)
	changeHandler: (e: React.ChangeEvent<{value: string}>) => void
}

export function useStringStateAgg(initialState: string, name: string='') : IStringStateAgg {
	const [value, setter] = useState(initialState);
	return {
		name,
		value,
		setter,
		makeSetter: v => makeSetterFn(v, setter),
		changeHandler: (e: React.ChangeEvent<any>) => {
			e.stopPropagation();
			setter('' + valueFromEvent(e));
		},
	};
}
